"use client";

import clsx from "clsx";

/**
 * A6 — Brand micro-illustration: a stylised gantry crane for T2 4.14 +
 * T3 5.9 (SmartLogistics shipment screens). Pairs with the cold-chain
 * milestone timeline so the "Loading · Departure · Arrival" beats read
 * visually as port operations, not just text. Mirrors the
 * LogisticsInsurancePicker's Maersk container-ship visual treatment.
 *
 * Two-mode: when the loading milestone has fired, the crane shows a
 * container suspended mid-lift (animated y-offset via CSS for subtle
 * motion). Before loading fires, the crane sits empty.
 */
export function CraneIllustration({
  loaded,
  quayLabel = "Jebel Ali quay 4",
  className,
}: {
  loaded?: boolean;
  /** Quay caption shown in the top-left of the tile. Defaults to the
   *  T1/T2 destination quay; T3 callsites override with the AE-side
   *  origin quay. */
  quayLabel?: string;
  className?: string;
}) {
  return (
    <div className={clsx("rounded-md border border-state-traced/30 bg-orbitra-ink/40 p-3", className)}>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">
          Gantry · {quayLabel}
        </p>
        <p className="font-mono text-[9px] text-orbitra-muted">
          {loaded ? "container · lifting" : "ready · awaiting"}
        </p>
      </div>
      <div className="relative mt-2 h-20 overflow-hidden rounded-md">
        {/* Quayside band */}
        <div className="absolute inset-x-0 bottom-0 h-5 bg-gradient-to-b from-orbitra-teal-deep/40 to-orbitra-ink/80" />
        {/* Sea band peeking on the right */}
        <div className="absolute bottom-5 right-0 h-1 w-1/3 bg-orbitra-teal-deep/30" />

        <svg viewBox="0 0 320 80" className="absolute inset-0 h-full w-full">
          {/* Gantry crane frame — two A-shaped legs on a quay */}
          {/* Left leg */}
          <line x1="80"  y1="68" x2="100" y2="22" stroke="var(--state-traced)" strokeWidth="2" />
          <line x1="120" y1="68" x2="100" y2="22" stroke="var(--state-traced)" strokeWidth="2" />
          {/* Right leg */}
          <line x1="200" y1="68" x2="220" y2="22" stroke="var(--state-traced)" strokeWidth="2" />
          <line x1="240" y1="68" x2="220" y2="22" stroke="var(--state-traced)" strokeWidth="2" />
          {/* Leg cross-bracing */}
          <line x1="90"  y1="55" x2="110" y2="55" stroke="var(--state-traced)" strokeWidth="1" opacity="0.6" />
          <line x1="210" y1="55" x2="230" y2="55" stroke="var(--state-traced)" strokeWidth="1" opacity="0.6" />

          {/* Horizontal boom across the top */}
          <line x1="80" y1="22" x2="260" y2="22" stroke="var(--state-traced)" strokeWidth="2.5" />
          {/* Boom extension out over the ship */}
          <line x1="260" y1="22" x2="290" y2="22" stroke="var(--state-traced)" strokeWidth="2" />

          {/* Trolley / hoist on the boom */}
          <rect x="160" y="18" width="14" height="6" fill="var(--state-traced)" rx="1" />

          {/* Hoist cable + container — shown when loaded */}
          {loaded ? (
            <g>
              {/* Cable */}
              <line x1="167" y1="24" x2="167" y2="44" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
              {/* Hook hardware */}
              <rect x="164" y="44" width="6" height="2" fill="rgba(255,255,255,0.7)" />
              {/* Suspended container — Orbitra-themed accent */}
              <rect x="156" y="46" width="22" height="12" fill="#D14B3D" />
              <rect x="156" y="46" width="22" height="2" fill="rgba(0,0,0,0.3)" />
              {/* Container ribs */}
              <line x1="161" y1="48" x2="161" y2="58" stroke="rgba(0,0,0,0.2)" strokeWidth="0.5" />
              <line x1="166" y1="48" x2="166" y2="58" stroke="rgba(0,0,0,0.2)" strokeWidth="0.5" />
              <line x1="171" y1="48" x2="171" y2="58" stroke="rgba(0,0,0,0.2)" strokeWidth="0.5" />
              <text x="167" y="55" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="3.5" fill="rgba(255,255,255,0.9)" letterSpacing="0.5">
                28 t
              </text>
            </g>
          ) : (
            <line x1="167" y1="24" x2="167" y2="34" stroke="rgba(255,255,255,0.25)" strokeWidth="0.8" />
          )}

          {/* Stacked containers waiting on the quay */}
          <rect x="40"  y="58" width="14" height="10" fill="#1A8A6F" />
          <rect x="56"  y="58" width="14" height="10" fill="#E2A23B" />
          <rect x="40"  y="48" width="14" height="10" fill="#D14B3D" />

          {/* Quay shadow under the crane */}
          <ellipse cx="160" cy="72" rx="100" ry="2" fill="rgba(0,0,0,0.3)" />
        </svg>
      </div>
    </div>
  );
}
